import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    LIST, VIEW, ADD, EDIT, DELETE, ACTIVE, INACTIVE,
} from "const"
import { deepCopy, hasAccess } from "client/utils"
import {
    PRM_PERMISSIONS,
    StoreType,
    addModal,
    updateAccountPermission,
    toggleAccountPermissionCustomizable,
    setAccountPermissionsUserSelected,
    setAccountPermissionsGroupSelected,
} from "client/store"
import {
    AccountPermissionRow,
    AccountPermissionItem,
    PermissionType,
} from "client/store/utils"
import {
    PermissionTableColumn,
    PermissionList,
    PermissionListItem,
    PermissionListAddItem,
    PermissionLabel,
    Permission,
    DeletePermissionButton,
    StatusSwitcher,
} from "client/components"

import NotAssigned from "./notAssigned"

const Columns = (
    accountId: number,
) => {
    const groupList = useSelector((state: StoreType) => state.group.groupList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const editAccess = hasAccess(PRM_PERMISSIONS, EDIT)

    const permissionFormatter = (
        section: string,
        permissionId: string,
        permissionType: PermissionType,
        permission: AccountPermissionRow,
    ) => {
        const permissionItem = permission[permissionType]
        if (!permissionItem) return null // unused permission

        const { groups, users } = permissionItem
        const allowedToEdit = editAccess
            ? userData.accountId === 1 || permission.customizable
            : false

        return (
            <PermissionList>
                {
                    groups.map((groupId) => {
                        const group = groupList[groupId]
                        return group && (
                            <PermissionListItem key={groupId}>
                                <PermissionLabel>{`${lang.group}:`}</PermissionLabel>
                                <Permission>{group.name}</Permission>
                                {
                                    allowedToEdit && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: AccountPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.groups = groups.filter((id) => id !== groupId)
                                                dispatch(updateAccountPermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    users.map((userId) => {
                        const user = userList[userId]
                        return user && (
                            <PermissionListItem key={userId}>
                                <PermissionLabel>{`${lang.user}:`}</PermissionLabel>
                                <Permission>{`${user.firstName} ${user.lastName}`}</Permission>
                                {
                                    allowedToEdit && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: AccountPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.users = users.filter((id) => id !== userId)
                                                dispatch(updateAccountPermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    allowedToEdit && (
                        <PermissionListAddItem
                            onClick={() => {
                                dispatch(setAccountPermissionsUserSelected(null))
                                dispatch(setAccountPermissionsGroupSelected(null))
                                dispatch(addModal({
                                    key: "notAssignedPermissions",
                                    content: <NotAssigned
                                        accountId={accountId}
                                        section={section}
                                        permissionId={permissionId}
                                        permissionType={permissionType}
                                        permissionItem={permissionItem}
                                    />,
                                }))
                            }}
                        >
                            {`+ ${lang.add}`}
                        </PermissionListAddItem>
                    )
                }
            </PermissionList>
        )
    }

    const cellMinWidth = "200px"
    const columns: PermissionTableColumn[] = [
        {
            field: "name",
            header: lang.name,
            cellMinWidth,
            cellMaxWidth: "300px",
            formatter: (_section, _permissionId, permission: AccountPermissionRow) => permission.title,
        },
        {
            field: LIST,
            header: lang.list,
            cellMinWidth,
            formatter: (section, permissionId, permission: AccountPermissionRow) => (
                permissionFormatter(section, permissionId, LIST, permission)
            ),
        },
        {
            field: VIEW,
            header: lang.view,
            cellMinWidth,
            formatter: (section, permissionId, permission: AccountPermissionRow) => (
                permissionFormatter(section, permissionId, VIEW, permission)
            ),
        },
        {
            field: ADD,
            header: lang.add,
            cellMinWidth,
            formatter: (section, permissionId, permission: AccountPermissionRow) => (
                permissionFormatter(section, permissionId, ADD, permission)
            ),
        },
        {
            field: EDIT,
            header: lang.edit,
            cellMinWidth,
            formatter: (section, permissionId, permission: AccountPermissionRow) => (
                permissionFormatter(section, permissionId, EDIT, permission)
            ),
        },
        {
            field: DELETE,
            header: lang.delete,
            cellMinWidth,
            formatter: (section, permissionId, permission: AccountPermissionRow) => (
                permissionFormatter(section, permissionId, DELETE, permission)
            ),
        },
    ]
    if (userData.accountId === 1) {
        columns.push({
            field: "customizable",
            header: lang.customizable,
            cellWidth: "100px",
            cellMinWidth: "100px",
            centerAlign: true,
            formatter: (section, permissionId, permission: AccountPermissionRow) => (
                <StatusSwitcher
                    status={permission.customizable ? ACTIVE : INACTIVE}
                    onClick={() => dispatch(toggleAccountPermissionCustomizable(section, permissionId))}
                />
            ),
        })
    }

    return columns
}
export default Columns
